/**
 * IncidentManager — the ALDS domain layer.
 *
 * An "incident" composes existing Surface Mesh primitives:
 *   - a ThreadManager thread for live coordination
 *   - (optional) a FunnelManager submission for the structured intake data
 *   - (optional) Vault manifests for attached evidence (Phase 5)
 *   - GovernanceManager audit entries for every state transition
 *
 * Per-device tables: incident_meta, incident_assignments, roster_status.
 * The Thread and Funnel submission a row references carry the synced
 * truth — incident_meta is just the local denorm for fast listing.
 */

import { blake3 } from '@noble/hashes/blake3';
import { v4 as uuid } from 'uuid';

import type { AppContext } from '../app';
import { signEnvelope, type SignedEnvelope } from '../envelope/Envelope';
import { RecoveryManager } from '../recovery/RecoveryManager';
import { ThreadManager } from '../threads/ThreadManager';
import { Vault } from '../vault/Vault';
import type {
  AuditLogRecord,
  IncidentMetaRecord,
  IncidentAssignmentRecord,
  MessageCacheRecord,
  MlsStateRecord,
  RosterStatusRecord,
  GeoPointRow,
} from '../storage/schema';

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC TYPES (re-exported from schema for ergonomic consumer imports)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Built-in category values. Plugins (Phase 17) may extend this set; the
 * stored category is any string the active plugin allows. The union is
 * here for documentation + IDE autocomplete on the basic set.
 */
export type IncidentCategoryBuiltin =
  | 'medical' | 'safety' | 'utility' | 'property' | 'welfare-check' | 'other';
export type IncidentCategory = IncidentCategoryBuiltin | (string & {});

export type IncidentSeverityBuiltin =
  | 'life-threat' | 'urgent' | 'assistance' | 'info';
export type IncidentSeverity = IncidentSeverityBuiltin | (string & {});

export type IncidentStatus =
  | 'open' | 'triaged' | 'dispatched' | 'on-scene' | 'resolved' | 'cancelled';

export type AssignmentStatus =
  | 'pending' | 'accepted' | 'declined' | 'completed' | 'released';

export type GeoPoint = GeoPointRow;
export type IncidentMeta = IncidentMetaRecord;
export type IncidentAssignment = IncidentAssignmentRecord;
export type RosterStatus = RosterStatusRecord;

export interface CreateIncidentOptions {
  category: IncidentCategory;
  proposedSeverity: IncidentSeverity;
  narrative?: string;
  location?: GeoPoint;
  submissionId?: string;
  /**
   * Optional Phase 11 membership-proof attestation. When set, the manager
   * records `verified: true|false` on the incident based on the prover's
   * verification result. Invalid proofs are NOT rejected — the design
   * accepts un-verified reports but flags them on the board.
   */
  membershipProof?: {
    /** Hex nullifier from the proof. */
    nullifier: string;
    /** Already-computed verification result (caller verifies before this call). */
    verified: boolean;
    impl: 'plaintext' | 'semaphore';
  };
}

export interface CreateIncidentResult {
  incidentId: string;
  threadId: string;
}

export interface ListIncidentsFilter {
  status?: IncidentStatus | IncidentStatus[];
  severity?: IncidentSeverity | IncidentSeverity[];
  near?: GeoPoint;
  limit?: number;
}

export interface AttachEvidenceOptions {
  incidentId: string;
  filename: string;
  contentType: string;
  bytes: Uint8Array;
}

export interface AttachEvidenceResult {
  manifestId: string;
  chunkCount: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

/** Allowed status transitions. Source → set of allowed targets.
 *  Note: open → dispatched is permitted because acceptAssignment auto-fast-
 *  forwards through triage when a responder picks up an un-triaged report
 *  directly (citizen-to-citizen mutual-aid path with no dispatcher in the
 *  loop). Caught by STFRP (commit when the harness landed). */
const STATUS_TRANSITIONS: Record<IncidentStatus, ReadonlySet<IncidentStatus>> = {
  open:       new Set(['triaged', 'dispatched', 'cancelled']),
  triaged:    new Set(['dispatched', 'cancelled', 'open']),  // can roll back to open
  dispatched: new Set(['on-scene', 'resolved', 'cancelled']),
  'on-scene': new Set(['resolved', 'cancelled']),
  resolved:   new Set([]),  // terminal
  cancelled:  new Set([]),  // terminal
};

/** ~50m grid in latitude degrees (varies by latitude; close enough for v1). */
const GEO_GRID_DEG = 0.00045;

// ─────────────────────────────────────────────────────────────────────────────
// MANAGER
// ─────────────────────────────────────────────────────────────────────────────

export class IncidentManager {
  readonly #ctx: AppContext;

  constructor(ctx: AppContext) {
    this.#ctx = ctx;
  }

  /**
   * Create a new incident. Spins up a coordinating Thread, persists the
   * IncidentMeta, audits the creation.
   */
  async createIncident(opts: CreateIncidentOptions): Promise<CreateIncidentResult> {
    const ctx = this.#ctx;
    const incidentId = uuid();
    const tm = new ThreadManager(ctx);

    // Bind the thread to a per-incident context. In hierarchical mode this
    // gives each incident its own HKDF-derived sub-key — callers can stay
    // unlinkable across incidents.
    const incidentContext = {
      environment: 'chat' as const,
      purpose: `incident-${incidentId}`,
    };
    const contextKey = `${incidentContext.environment}:${incidentContext.purpose}`;

    const thread = await tm.createThread({
      name: `incident-${incidentId}`,
      kind: 'chat',
      context: incidentContext,
    });

    const reporterPubkeyBytes = await ctx.identity.pubkey(incidentContext);
    const reporterPubkey = bytesToHex(reporterPubkeyBytes);
    const now = new Date().toISOString();

    const membershipProof = opts.membershipProof !== undefined
      ? {
          nullifier: opts.membershipProof.nullifier,
          verified: opts.membershipProof.verified,
          verifiedAt: now,
          impl: opts.membershipProof.impl,
        }
      : undefined;

    const record: IncidentMetaRecord = {
      incidentId,
      context: contextKey,
      threadId: thread.threadId,
      category: opts.category,
      proposedSeverity: opts.proposedSeverity,
      status: 'open',
      createdAt: now,
      updatedAt: now,
      reporterPubkey,
      ...(opts.submissionId !== undefined ? { submissionId: opts.submissionId } : {}),
      ...(opts.narrative !== undefined ? { narrative: opts.narrative } : {}),
      ...(opts.location !== undefined ? { location: coarsenGeo(opts.location) } : {}),
      ...(membershipProof !== undefined ? { membershipProof } : {}),
    };

    await ctx.storage.db.incident_meta.put(record);

    await this.audit({
      action: 'incident.created',
      context: contextKey,
      details: {
        incidentId,
        threadId: thread.threadId,
        category: opts.category,
        proposedSeverity: opts.proposedSeverity,
        hasLocation: opts.location !== undefined,
        membership: membershipProof
          ? { verified: membershipProof.verified, impl: membershipProof.impl, nullifierPrefix: membershipProof.nullifier.slice(0, 12) }
          : null,
      },
    });

    return { incidentId, threadId: thread.threadId };
  }

  /**
   * List incidents matching the filter. Sorted by createdAt desc by default;
   * when filter.near is set, sorted by ascending approximate distance.
   */
  async listIncidents(filter: ListIncidentsFilter = {}): Promise<IncidentMetaRecord[]> {
    const db = this.#ctx.storage.db;
    const limit = filter.limit ?? 100;

    let rows = await db.incident_meta.toArray();

    if (filter.status !== undefined) {
      const allowed = Array.isArray(filter.status)
        ? new Set(filter.status)
        : new Set([filter.status]);
      rows = rows.filter((r) => allowed.has(r.status));
    }
    if (filter.severity !== undefined) {
      const allowed = Array.isArray(filter.severity)
        ? new Set(filter.severity)
        : new Set([filter.severity]);
      rows = rows.filter((r) => allowed.has(r.confirmedSeverity ?? r.proposedSeverity));
    }

    if (filter.near !== undefined) {
      const target = filter.near;
      rows.sort((a, b) => approxDistance(a.location, target) - approxDistance(b.location, target));
    } else {
      rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }

    return rows.slice(0, limit);
  }

  async getIncident(incidentId: string): Promise<IncidentMetaRecord | undefined> {
    return this.#ctx.storage.db.incident_meta.get(incidentId);
  }

  /**
   * Update status. Enforces the transition table. Audited.
   */
  async updateStatus(incidentId: string, status: IncidentStatus): Promise<void> {
    const db = this.#ctx.storage.db;
    const row = await db.incident_meta.get(incidentId);
    if (row === undefined) {
      throw new Error(`IncidentManager.updateStatus: unknown incident ${incidentId}`);
    }
    if (row.status === status) return;
    const allowed = STATUS_TRANSITIONS[row.status];
    if (!allowed.has(status)) {
      throw new Error(
        `IncidentManager.updateStatus: illegal transition ${row.status} → ${status}`,
      );
    }
    const now = new Date().toISOString();
    await db.incident_meta.update(incidentId, { status, updatedAt: now });

    await this.audit({
      action: 'incident.status-changed',
      context: row.context,
      details: { incidentId, from: row.status, to: status },
    });
  }

  /**
   * Dispatcher-side: confirm (or override) the caller's proposed severity.
   */
  async confirmSeverity(incidentId: string, severity: IncidentSeverity): Promise<void> {
    const db = this.#ctx.storage.db;
    const row = await db.incident_meta.get(incidentId);
    if (row === undefined) {
      throw new Error(`IncidentManager.confirmSeverity: unknown incident ${incidentId}`);
    }
    const now = new Date().toISOString();
    await db.incident_meta.update(incidentId, { confirmedSeverity: severity, updatedAt: now });

    await this.audit({
      action: 'incident.severity-confirmed',
      context: row.context,
      details: {
        incidentId,
        proposed: row.proposedSeverity,
        confirmed: severity,
        override: row.proposedSeverity !== severity,
      },
    });
  }

  /**
   * Invite a responder. Creates a pending IncidentAssignment row. Audited.
   * Caller is responsible for delivering the dispatch (thread invite or
   * direct broadcast) — this method only records the assignment.
   */
  async assignResponder(incidentId: string, responderPubkey: string, role?: string): Promise<IncidentAssignmentRecord> {
    const db = this.#ctx.storage.db;
    const incident = await db.incident_meta.get(incidentId);
    if (incident === undefined) {
      throw new Error(`IncidentManager.assignResponder: unknown incident ${incidentId}`);
    }
    const now = new Date().toISOString();
    const record: IncidentAssignmentRecord = {
      id: `${incidentId}:${responderPubkey}`,
      incidentId,
      responderPubkey,
      status: 'pending',
      assignedAt: now,
      ...(role !== undefined ? { role } : {}),
    };
    await db.incident_assignments.put(record);

    await this.audit({
      action: 'incident.responder-assigned',
      context: incident.context,
      details: { incidentId, responderPubkey, role },
    });

    // Phase 27.8: wake the responder via the operator's push relay.
    // Fire-and-forget; failure never blocks assignment. Lazy import keeps
    // incident → credentials module direction one-way.
    void (async () => {
      try {
        const { getWakeupNotifier } = await import('../credentials/WakeupNotifier');
        await getWakeupNotifier(this.#ctx).wake({
          targetPubkey: responderPubkey,
          kind: 'alds-incident-assigned',
          details: { incidentId, ...(role !== undefined ? { role } : {}) },
        });
      } catch { /* swallow — best-effort */ }
    })();

    return record;
  }

  /**
   * Responder-side: accept an assignment. Flips status to 'accepted' and
   * (if incident is still 'triaged' or 'open') moves incident status to
   * 'dispatched'.
   *
   * Identifies the responder via the stable hub:roster pubkey — the same
   * one written by setOwnShift, so the assignment row written by the
   * dispatcher (which used roster pubkeys to pick a responder) lines up.
   */
  async acceptAssignment(incidentId: string): Promise<void> {
    const db = this.#ctx.storage.db;
    const myPubkey = await this.#ownResponderPubkey();
    const id = `${incidentId}:${myPubkey}`;
    const row = await db.incident_assignments.get(id);
    if (row === undefined) {
      throw new Error(`IncidentManager.acceptAssignment: no pending assignment for ${myPubkey} on ${incidentId}`);
    }
    if (row.status !== 'pending') {
      throw new Error(`IncidentManager.acceptAssignment: assignment is ${row.status}, not pending`);
    }
    const now = new Date().toISOString();
    await db.incident_assignments.update(id, { status: 'accepted', acceptedAt: now });

    const incident = await db.incident_meta.get(incidentId);
    if (incident?.status === 'triaged' || incident?.status === 'open') {
      await this.updateStatus(incidentId, 'dispatched');
    } else {
      await this.audit({
        action: 'incident.assignment-accepted',
        context: incident?.context ?? '',
        details: { incidentId, responderPubkey: myPubkey },
      });
    }
  }

  async declineAssignment(incidentId: string): Promise<void> {
    const db = this.#ctx.storage.db;
    const myPubkey = await this.#ownResponderPubkey();
    const id = `${incidentId}:${myPubkey}`;
    const row = await db.incident_assignments.get(id);
    if (row === undefined) {
      throw new Error(`IncidentManager.declineAssignment: no assignment for ${myPubkey} on ${incidentId}`);
    }
    const now = new Date().toISOString();
    await db.incident_assignments.update(id, { status: 'declined', declinedAt: now });

    const incident = await db.incident_meta.get(incidentId);
    await this.audit({
      action: 'incident.assignment-declined',
      context: incident?.context ?? '',
      details: { incidentId, responderPubkey: myPubkey },
    });
  }

  async listAssignments(incidentId: string): Promise<IncidentAssignmentRecord[]> {
    return this.#ctx.storage.db.incident_assignments
      .where('incidentId').equals(incidentId)
      .toArray();
  }

  /** Returns assignment rows for the local responder, sorted most-recent first. */
  async listMyAssignments(filter: { status?: AssignmentStatus | AssignmentStatus[] } = {}): Promise<IncidentAssignmentRecord[]> {
    const myPubkey = await this.#ownResponderPubkey();
    let rows = await this.#ctx.storage.db.incident_assignments
      .where('responderPubkey').equals(myPubkey)
      .toArray();
    if (filter.status !== undefined) {
      const allowed = Array.isArray(filter.status) ? new Set(filter.status) : new Set([filter.status]);
      rows = rows.filter((r) => allowed.has(r.status));
    }
    rows.sort((a, b) => b.assignedAt.localeCompare(a.assignedAt));
    return rows;
  }

  /** Returns the local responder's stable pubkey (hex) under the hub:roster context. */
  async ownResponderPubkey(): Promise<string> {
    return this.#ownResponderPubkey();
  }

  async #ownResponderPubkey(): Promise<string> {
    const bytes = await this.#ctx.identity.pubkey({
      environment: 'hub',
      purpose: 'roster',
    });
    return bytesToHex(bytes);
  }

  /**
   * Append an envelope to the sync stream; if that fails (network is down,
   * peer not reachable), queue it via RecoveryManager so it can be drained
   * later. Local writes always succeeded by the time this is called — we
   * only risk losing the outbound broadcast.
   */
  async #publishOrQueue(envelope: SignedEnvelope, streamId: string, contextKey: string): Promise<void> {
    try {
      await this.#ctx.sync.append(streamId, envelope);
    } catch (err) {
      // Network/sync error — queue for retry. Audited so operators can see
      // the queue grew.
      try {
        const rm = new RecoveryManager(this.#ctx);
        await rm.enqueueOutbound({ streamId, envelope, context: contextKey });
        await this.audit({
          action: 'incident.outbound-queued',
          context: contextKey,
          details: {
            streamId,
            envelopeHash: envelope.hash,
            reason: err instanceof Error ? err.message : String(err),
          },
        });
      } catch (queueErr) {
        // eslint-disable-next-line no-console
        console.warn(
          `IncidentManager.#publishOrQueue: enqueue failed too: ${queueErr instanceof Error ? queueErr.message : String(queueErr)}`,
        );
        throw err;  // surface the original sync error
      }
    }
  }

  /**
   * Broadcast a "backup requested" audit entry tied to an incident. The
   * actual fan-out to nearby responders rides on the existing thread/sync
   * channel and lands in Phase 6; for v0 this records the request so it
   * shows up in env-audit and in any later forensic review.
   */
  async requestBackup(incidentId: string, reason?: string): Promise<void> {
    const db = this.#ctx.storage.db;
    const incident = await db.incident_meta.get(incidentId);
    if (incident === undefined) {
      throw new Error(`IncidentManager.requestBackup: unknown incident ${incidentId}`);
    }
    const myPubkey = await this.#ownResponderPubkey();
    await this.audit({
      action: 'incident.backup-requested',
      context: incident.context,
      details: {
        incidentId,
        requesterPubkey: myPubkey,
        ...(reason !== undefined && reason !== '' ? { reason } : {}),
      },
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Roster (caller-side and responder-side use the same table)
  // ─────────────────────────────────────────────────────────────────────────

  /** Mark the local responder on/off shift. Per-device only. */
  async setOwnShift(onShift: boolean): Promise<void> {
    const db = this.#ctx.storage.db;
    const myPubkey = await this.#ownResponderPubkey();
    const now = new Date().toISOString();
    const existing = await db.roster_status.get(myPubkey);
    const record: RosterStatusRecord = {
      pubkey: myPubkey,
      onShift: onShift ? 1 : 0,
      lastActive: now,
      ...(existing?.capabilities !== undefined ? { capabilities: existing.capabilities } : {}),
    };
    await db.roster_status.put(record);
  }

  /** Returns responders currently on shift, most-recently-active first. */
  async listOnShift(): Promise<RosterStatusRecord[]> {
    return this.#ctx.storage.db.roster_status
      .where('onShift').equals(1)
      .reverse()
      .sortBy('lastActive');
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Evidence — Phase 5 work. See docs/alds-roadmap.md.
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Attach a photo (or other media) to an incident.
   *
   * Steps:
   *  1. Load the incident's coordination thread + its MLS group state.
   *  2. Chunk + AES-GCM-encrypt the bytes via Vault. The seed is group-
   *     encrypted (so only group members can derive chunk keys); this
   *     advances the MLS state by one application message.
   *  3. Sign a `manifest-link` envelope carrying the manifest JSON and
   *     append it to the thread's sync stream.
   *  4. Persist the advanced MLS state + write a message_cache row of
   *     kind 'manifest-link' (text = filename) so the local UI sees it
   *     without waiting for the inbox to round-trip it.
   *  5. Bump threads_index.lastActivityAt.
   *  6. Audit.
   *
   * v1 scope: photos only. Audio/video/large files deferred to v2.
   */
  async attachEvidence(opts: AttachEvidenceOptions): Promise<AttachEvidenceResult> {
    const ctx = this.#ctx;
    const db = ctx.storage.db;

    const incident = await db.incident_meta.get(opts.incidentId);
    if (incident === undefined) {
      throw new Error(`IncidentManager.attachEvidence: unknown incident ${opts.incidentId}`);
    }
    const threadId = incident.threadId;
    const tm = new ThreadManager(ctx);
    const groupState = await tm._loadGroupState(threadId);

    // Vault.store advances the MLS state once (the seed wrap).
    const vault = new Vault({ groupEncryption: ctx.groupEnc, storage: ctx.storage });
    const { manifest, groupStateAfterStore } = await vault.store({
      groupState,
      filename: opts.filename,
      contentType: opts.contentType,
      bytes: opts.bytes,
    });

    // Sign + append the manifest envelope.
    const incidentContext = {
      environment: 'chat' as const,
      purpose: `incident-${opts.incidentId}`,
    };
    const envelope = await signEnvelope({
      identity: ctx.identity,
      context: incidentContext,
      contentType: 'application/json; profile=vault-manifest',
      payload: JSON.stringify({ kind: 'manifest-link', manifest }),
      lamport: 0,
      parentHash: '',
    });
    const streamId = `mesh-thread-${threadId}`;
    await this.#publishOrQueue(envelope, streamId, incident.context);

    const now = new Date().toISOString();

    // Persist the new MLS state + local message_cache row + activity bump.
    const newMlsRecord: MlsStateRecord = {
      threadId,
      context: incident.context,
      epoch: groupStateAfterStore.epoch,
      memberCount: groupStateAfterStore.memberCount,
      cipherSuite: groupStateAfterStore.impl === 'plaintext' ? 'plaintext' : 'mls',
      ciphertext: await ctx.storage.encryptForSession(groupStateAfterStore.opaque),
      updatedAt: now,
    };

    // For the cache row, store the filename in ciphertext (the text users
    // see when listing messages). The full manifest lives on the envelope
    // stream — viewers fetch chunks via the envelope payload.
    const cacheCipher = await ctx.storage.encryptForSession(
      new TextEncoder().encode(opts.filename),
    );
    const cacheRecord: MessageCacheRecord = {
      hash: envelope.hash,
      context: incident.context,
      threadId,
      senderPubkey: envelope.header.signerPubkey,
      lamport: envelope.header.lamport,
      sentAt: envelope.header.timestamp,
      receivedAt: now,
      kind: 'manifest-link',
      ciphertext: cacheCipher,
    };

    await db.transaction(
      'rw',
      [db.mls_state, db.message_cache, db.threads_index],
      async () => {
        await db.mls_state.put(newMlsRecord);
        await db.message_cache.put(cacheRecord);
        const idx = await db.threads_index.get(threadId);
        if (idx !== undefined) {
          await db.threads_index.update(threadId, { lastActivityAt: now });
        }
      },
    );

    await this.audit({
      action: 'incident.evidence-attached',
      context: incident.context,
      details: {
        incidentId: opts.incidentId,
        manifestId: manifest.manifestId,
        filename: opts.filename,
        contentType: opts.contentType,
        chunkCount: manifest.chunks.length,
        totalSize: manifest.totalSize,
        envelopeHash: envelope.hash,
      },
    });

    return {
      manifestId: manifest.manifestId,
      chunkCount: manifest.chunks.length,
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Transparency export — redacted day log
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Build a redacted JSON export of incidents + signed audit entries over a
   * time window. Suitable for posting publicly or pinning to IPFS.
   *
   * Redactions:
   *  - Reporter / responder pubkeys are reduced to a 12-char BLAKE3 prefix.
   *  - Geo is coarsened further: lat/lng truncated to 2 decimals (~1.1 km
   *    grid) and accuracyM clamped to 1000.
   *  - Narrative text is preserved (operators may want to add a manual
   *    redaction pass before posting; that's out of scope for v1).
   *  - threadId, submissionId, manifestIds: kept (they reveal nothing
   *    without group keys but ARE useful for matching activity).
   */
  async exportDayLog(opts: { since?: string; until?: string } = {}): Promise<DayLogExport> {
    const db = this.#ctx.storage.db;
    const sinceISO = opts.since ?? new Date(0).toISOString();
    const untilISO = opts.until ?? new Date().toISOString();

    const incidents = (await db.incident_meta.toArray())
      .filter((r) => r.createdAt >= sinceISO && r.createdAt <= untilISO);
    const auditRows = await db.audit_log
      .where('category').equals('incidents')
      .toArray();
    const audits = auditRows.filter((a) => a.occurredAt >= sinceISO && a.occurredAt <= untilISO);

    return {
      kind: 'alds-day-log',
      version: 1,
      window: { since: sinceISO, until: untilISO },
      generatedAt: new Date().toISOString(),
      incidents: incidents.map((i) => redactIncident(i)),
      auditEntries: audits.map((a) => redactAudit(a)),
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Internal
  // ─────────────────────────────────────────────────────────────────────────

  private async audit(input: {
    action: string;
    context: string;
    details: Record<string, unknown>;
  }): Promise<void> {
    const ctx = this.#ctx;
    const occurredAt = new Date().toISOString();
    const canonical = JSON.stringify({
      category: 'incidents' as const,
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
    });
    const signature = await ctx.identity.sign(
      new TextEncoder().encode(canonical),
      { environment: 'chat', purpose: 'thread-default' },
    );
    const record: AuditLogRecord = {
      seq: undefined as unknown as number,
      category: 'incidents',
      action: input.action,
      context: input.context,
      details: input.details,
      occurredAt,
      signature,
    };
    try {
      await ctx.storage.db.audit_log.add(record);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`IncidentManager.audit: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i]!.toString(16).padStart(2, '0');
  }
  return out;
}

/** Snap to ~50m grid for privacy. lat/lng are coarsened independently. */
function coarsenGeo(p: GeoPoint): GeoPoint {
  return {
    lat: Math.round(p.lat / GEO_GRID_DEG) * GEO_GRID_DEG,
    lng: Math.round(p.lng / GEO_GRID_DEG) * GEO_GRID_DEG,
    accuracyM: Math.max(p.accuracyM, 50),
    fixAt: p.fixAt,
  };
}

/** Squared euclidean distance — cheap, good enough for sorting in v1. */
function approxDistance(a: GeoPoint | undefined, b: GeoPoint): number {
  if (a === undefined) return Number.POSITIVE_INFINITY;
  const dLat = a.lat - b.lat;
  const dLng = a.lng - b.lng;
  return dLat * dLat + dLng * dLng;
}

// ─────────────────────────────────────────────────────────────────────────────
// DAY LOG EXPORT TYPES + REDACTION
// ─────────────────────────────────────────────────────────────────────────────

export interface DayLogExport {
  kind: 'alds-day-log';
  version: 1;
  window: { since: string; until: string };
  generatedAt: string;
  incidents: RedactedIncident[];
  auditEntries: RedactedAudit[];
}

export interface RedactedIncident {
  incidentId: string;
  threadId: string;
  submissionId?: string;
  category: string;
  proposedSeverity: string;
  confirmedSeverity?: string;
  status: string;
  narrative?: string;
  /** Coarsened: 2 decimal places (~1.1km), accuracyM clamped to >= 1000. */
  location?: { lat: number; lng: number; accuracyM: number; fixAt: string };
  createdAt: string;
  updatedAt: string;
  /** Reporter identity reduced to a 12-char hash prefix. */
  reporterHash: string;
  /** When a membership proof was attached, the export shows whether verification
   *  passed and a 12-char nullifier prefix (full nullifier is unmasked-equivalent
   *  for the same caller across reports — leaks more than is desirable). */
  membership?: {
    verified: boolean;
    nullifierHashPrefix: string;
    impl: 'plaintext' | 'semaphore';
  };
}

export interface RedactedAudit {
  occurredAt: string;
  action: string;
  context: string;
  /** A hash-keyed copy of details with pubkey-like fields stripped. */
  details: Record<string, unknown>;
}

function hashShort(input: string): string {
  return bytesToHex(blake3(new TextEncoder().encode(input))).slice(0, 12);
}

function coarsenForExport(p: GeoPoint): { lat: number; lng: number; accuracyM: number; fixAt: string } {
  return {
    lat: Math.round(p.lat * 100) / 100,
    lng: Math.round(p.lng * 100) / 100,
    accuracyM: Math.max(p.accuracyM, 1000),
    fixAt: p.fixAt,
  };
}

function redactIncident(i: IncidentMetaRecord): RedactedIncident {
  return {
    incidentId: i.incidentId,
    threadId: i.threadId,
    ...(i.submissionId !== undefined ? { submissionId: i.submissionId } : {}),
    category: i.category,
    proposedSeverity: i.proposedSeverity,
    ...(i.confirmedSeverity !== undefined ? { confirmedSeverity: i.confirmedSeverity } : {}),
    status: i.status,
    ...(i.narrative !== undefined ? { narrative: i.narrative } : {}),
    ...(i.location !== undefined ? { location: coarsenForExport(i.location) } : {}),
    createdAt: i.createdAt,
    updatedAt: i.updatedAt,
    reporterHash: hashShort(i.reporterPubkey),
    ...(i.membershipProof !== undefined ? {
      membership: {
        verified: i.membershipProof.verified,
        nullifierHashPrefix: hashShort(i.membershipProof.nullifier),
        impl: i.membershipProof.impl,
      },
    } : {}),
  };
}

function redactAudit(a: AuditLogRecord): RedactedAudit {
  // Strip any field name ending in 'Pubkey' or named 'pubkey'; replace with
  // a hash prefix. Conservative — anything that LOOKS like an identity goes
  // through the hash.
  const safeDetails: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(a.details ?? {})) {
    if (/pubkey$/i.test(k) || k.toLowerCase() === 'pubkey') {
      if (typeof v === 'string' && v.length > 0) {
        safeDetails[`${k}Hash`] = hashShort(v);
      }
    } else if (k === 'location' && v !== null && typeof v === 'object') {
      safeDetails[k] = coarsenForExport(v as GeoPoint);
    } else {
      safeDetails[k] = v;
    }
  }
  return {
    occurredAt: a.occurredAt,
    action: a.action,
    context: a.context ?? '',
    details: safeDetails,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// ERROR TYPE
// ─────────────────────────────────────────────────────────────────────────────

export class NotImplemented extends Error {
  constructor(method: string) {
    super(`${method}: not implemented yet (Phase 1 of ALDS roadmap)`);
    this.name = 'NotImplemented';
  }
}
